import { useCallback, useMemo, useState } from 'react';
import type { Nullable } from 'corgi-types';

import { EventBusEvent } from 'common/enums/EventBus';
import { useSubscriptionCallback } from 'common/hooks/subscription';
import type { GraphqlNavigationItemProps } from 'common/types/NavigationItem';
import type { SideNavEvent } from 'common/utils/navigation';
import type { ContentNavigationTab } from '../types';
import { sortNavigation } from '../utils';

interface UseTabbedNavigationParams {
  tabs: ContentNavigationTab[];
  isEnabled: boolean;
}

export interface UseTabbedNavigationResult {
  tabs: ContentNavigationTab[];
  activeTab: ContentNavigationTab;
  activeTabIndex: number;
  isDefaultTab: boolean;
  setActiveTab: (label: string) => void;
  navigation: GraphqlNavigationItemProps;
  resetTabs: () => void;
}

export const useTabbedNavigation = ({
  tabs,
  isEnabled,
}: UseTabbedNavigationParams): Nullable<UseTabbedNavigationResult> => {
  const [activeTabLabel, setActiveTabLabel] = useState<string | null>(null);

  const handleToggleSideNav = useCallback((data: SideNavEvent) => {
    if (data.activeTab) {
      setActiveTabLabel(data.activeTab);
    }
  }, []);

  useSubscriptionCallback<SideNavEvent>(EventBusEvent.ToggleSideNav, handleToggleSideNav);

  const resetTabs = useCallback(() => setActiveTabLabel(null), []);

  const activeTabIndex = useMemo(() => {
    if (!tabs.length) return 0;
    if (activeTabLabel === null) return 0;
    const idx = tabs.findIndex(t => t.label === activeTabLabel);

    return idx >= 0 ? idx : 0;
  }, [tabs, activeTabLabel]);

  const navigation = useMemo<GraphqlNavigationItemProps>(() => {
    const tab = tabs[activeTabIndex];
    if (!tab?.children?.length) {
      return { name: '', children: [] };
    }

    return {
      name: tab.label,
      children: sortNavigation(tab.children as GraphqlNavigationItemProps[]),
    };
  }, [tabs, activeTabIndex]);

  if (!isEnabled || !tabs.length) {
    return null;
  }

  const activeTab = tabs[activeTabIndex];

  return {
    tabs,
    activeTab,
    activeTabIndex,
    isDefaultTab: activeTabIndex === 0,
    setActiveTab: setActiveTabLabel,
    navigation,
    resetTabs,
  };
};
